/**
 * Data APIs - Centralized API calls for template and research management
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8105';

// Generic API request function
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const authToken = token || localStorage.getItem('auth_token');
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, config);
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`API Error: ${response.status} - ${error}`);
  }

  return response.json();
}

// Template API Types
export interface TemplateCreateRequest {
  query: string;
  research_length: 'quick_scan' | 'targeted' | 'deep_dive';
  api_key?: string;
}

export interface TemplateQuestion {
  id: string;
  text: string;
  sources: string[];
}

export interface TemplateTopic {
  id: string;
  title: string;
  description?: string;
  questions: TemplateQuestion[];
  sources: Record<string, any>;
}

export interface TemplateStructure {
  query: string;
  research_length: string;
  clarifying_questions: Record<string, string>;
  topics: TemplateTopic[];
  metadata: {
    total_topics: number;
    questions_per_topic: number;
    estimated_total_questions: number;
  };
}

export interface TemplateCreateResponse {
  success: boolean;
  data: TemplateStructure;
  message: string;
}

export interface TemplateSaveRequest {
  name: string;
  description?: string;
  query: string;
  research_length: string;
  topics: TemplateTopic[];
  is_public?: boolean;
}

export interface TemplateSaveResponse {
  success: boolean;
  template_id: string;
  message: string;
}

export interface Template {
  id: string;
  template_name: string;
  description: string;
  initial_query: string;
  research_length: string;
  created_at: string;
  updated_at: string;
  topic_count: number;
  question_count: number;
}

export interface TemplatesResponse {
  success: boolean;
  templates: Template[];
  unique_topic_count: number;
}

export interface TemplateExecuteRequest {
  api_key?: string;
}

export interface TemplateExecuteResponse {
  success: boolean;
  session_id: string;
  template_name: string;
  query: string;
  message: string;
}

// Template API Functions
export const templateApi = {
  /**
   * Create template structure - generates clarifications, topics, questions, sources
   */
  async createTemplate(data: TemplateCreateRequest, token?: string): Promise<TemplateCreateResponse> {
    return apiRequest<TemplateCreateResponse>('/api/create-template', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  },

  /**
   * Save edited template to database
   */
  async saveTemplate(data: TemplateSaveRequest, token?: string): Promise<TemplateSaveResponse> {
    return apiRequest<TemplateSaveResponse>('/api/save-template', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
  },

  /**
   * Get user's saved templates
   */
  async getTemplates(): Promise<TemplatesResponse> {
    return apiRequest<TemplatesResponse>('/api/templates');
  },

  /**
   * Execute template to start research workflow
   */
  async executeTemplate(templateId: string, data: TemplateExecuteRequest): Promise<TemplateExecuteResponse> {
    return apiRequest<TemplateExecuteResponse>(`/api/templates/${templateId}/execute`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get template details with topics and questions
   */
  async getTemplateDetails(templateId: string, token?: string): Promise<TemplateStructure> {
    return apiRequest<TemplateStructure>(`/api/templates/${templateId}`, {
      method: 'GET',
    }, token);
  },

  /**
   * Update existing template
   */
  async updateTemplate(templateId: string, data: TemplateSaveRequest, token?: string): Promise<TemplateSaveResponse> {
    return apiRequest<TemplateSaveResponse>(`/api/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
  },

  /**
   * Delete template (soft delete)
   */
  async deleteTemplate(templateId: string, token?: string): Promise<{success: boolean}> {
    return apiRequest<{success: boolean}>(`/api/templates/${templateId}`, {
      method: 'DELETE',
    }, token);
  },
};

// Research API Types
export interface ResearchStartRequest {
  query: string;
  research_length: 'quick_scan' | 'targeted' | 'deep_dive';
  api_key?: string;
  message_type?: 'query';
}

// User Chat/Session API Types
export interface UserChat {
  id: string;
  title: string;
  initial_query: string;
  research_length: string;
  status: string;
  current_step: string;
  progress_percentage: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  template_name?: string;
}

export interface UserChatsResponse {
  success: boolean;
  chats: UserChat[];
}

export interface UserTemplate {
  id: string;
  template_name: string;
  description: string;
  initial_query: string;
  research_length: string;
  created_at: string;
  updated_at: string;
  topic_count: number;
  question_count: number;
  usage_status: 'used' | 'unused';
  execution_count: number;
}

export interface UserTemplatesResponse {
  success: boolean;
  templates: UserTemplate[];
}

export interface SessionDataResponse {
  success: boolean;
  data: {
    session: {
      id: string;
      title: string;
      initial_query: string;
      refined_query?: string;
      status: string;
      research_length: string;
      current_step?: string;
      progress_percentage: number;
      template_name?: string;
      created_at: string;
      updated_at: string;
      completed_at?: string;
    };
    summary?: {
      summary: string;
      summary_metadata: any;
    };
    clarification?: {
      questions: Record<string, string>;
      answers?: Record<string, string>;
    };
    topics: Array<{
      topic_id: string;
      topic_name: string;
      topic_description?: string;
      questions: Array<{
        id: string;
        question: string;
        answer?: string;
        preferred_sources: string[];
        answer_citations: string[];
        answer_confidence_score?: number;
        answered_at?: string;
      }>;
    }>;
  };
}

// Research API Functions
export const researchApi = {
  /**
   * Start research workflow (existing functionality)
   */
  async startResearch(data: ResearchStartRequest): Promise<any> {
    // This will use existing WebSocket flow
    return { success: true, message: 'Use WebSocket for research workflow' };
  },
};

// User Data API Functions
export const userDataApi = {
  /**
   * Get user's chat history (research sessions)
   */
  async getUserChats(
    limit: number = 50, 
    offset: number = 0, 
    statusFilter?: string,
    token?: string
  ): Promise<UserChatsResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      ...(statusFilter && { status_filter: statusFilter })
    });
    
    return apiRequest<UserChatsResponse>(`/api/user-chats?${params}`, {}, token);
  },

  /**
   * Get user's saved templates
   */
  async getUserTemplates(
    limit: number = 50,
    offset: number = 0,
    token?: string
  ): Promise<UserTemplatesResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });
    
    return apiRequest<UserTemplatesResponse>(`/api/user-templates?${params}`, {}, token);
  },

  /**
   * Get complete research data for a specific session
   */
  async getSessionData(
    sessionId: string,
    token?: string
  ): Promise<SessionDataResponse> {
    return apiRequest<SessionDataResponse>(`/api/sessions/${sessionId}`, {}, token);
  },

  /**
   * Delete research session (soft delete)
   */
  async deleteResearchSession(
    sessionId: string,
    token?: string
  ): Promise<{success: boolean; message: string; session_id: string}> {
    return apiRequest<{success: boolean; message: string; session_id: string}>(`/api/sessions/${sessionId}`, {
      method: 'DELETE',
    }, token);
  },
};

// Error handling utility
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Hook for API error handling
export function handleApiError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}